import { useEffect, useState } from "react";
import { storage } from "../../state/storage.js";
import {
  createScript,
  type Script,
  type ScriptCapabilities,
  type ScriptHook,
} from "@roqeet/core";
import { CodeEditor } from "./CodeEditor.js";
import { ScriptTestPanel } from "./ScriptTestPanel.js";
import { VersionHistoryPanel } from "./VersionHistoryPanel.js";
import { scriptPath } from "../../state/version-actions.js";
import { useT } from "../../i18n/use-t.js";

/**
 * 트리거 스크립트 관리(결정 #4/#5/#6/#17). 제작자 로직은 격리 샌드박스에서
 * 실행되며(ambient authority 0), 호스트가 주입한 capability API만 쓸 수 있다.
 *  - JavaScript: QuickJS-WASM 샌드박스.
 *  - Lua: wasmoon(Lua 5.4) 샌드박스(io/os/package/debug 미개방, Risu 호환 #6).
 *
 * 지원 범위(죽은 UI 금지):
 *  - 훅: on-user-send(사용자 입력 변형), after-response(AI 출력 변형).
 *  - capability: variables(safe, getVar/setVar) + network(elevated, 중개 fetch,
 *    도메인 allowlist + 동의 필요). storage는 후속.
 *  - API 전역: context.{hook,text}, setResult(t), getVar/setVar, getChatMessages,
 *    log, random(), now(). setResult로 변형 결과를 호스트에 돌려준다.
 *
 * 만든 뒤 캐릭터 편집에서 연결하면(scriptIds) 전송 경로에서 자동 실행된다.
 */
const SUPPORTED_HOOKS: ScriptHook[] = ["on-user-send", "after-response"];

const LANG_LABELS: Record<"javascript" | "lua", string> = {
  javascript: "JavaScript",
  lua: "Lua",
};

type Translate = ReturnType<typeof useT>["t"];

function hookLabel(hook: ScriptHook, t: Translate): string {
  switch (hook) {
    case "on-user-send":
      return t("js.hookUserSend");
    case "after-response":
      return t("js.hookAfterResponse");
    default:
      return hook;
  }
}

function defaultJsTemplate(t: Translate): string {
  return `// ${t("js.template.context")}
// ${t("js.template.result")}
let t = context.text;
setResult(t.trim());`;
}

function defaultLuaTemplate(t: Translate): string {
  return `-- ${t("js.template.context")}
-- ${t("js.template.result")}
setResult((context.text):gsub("^%s+", ""):gsub("%s+$", ""))`;
}

export function JsScriptsSection() {
  const { t } = useT();
  const [scripts, setScripts] = useState<Script[]>([]);
  const [editing, setEditing] = useState<Script | "new" | null>(null);

  const reload = async () => {
    const all = await storage.scripts.list();
    setScripts(all.filter((s) => s.language === "javascript" || s.language === "lua"));
  };
  useEffect(() => {
    void reload();
  }, []);

  const remove = async (s: Script) => {
    if (!confirm(t("js.deleteConfirm", { name: s.name }))) return;
    await storage.scripts.delete(s.id);
    await reload();
  };

  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {t("js.title")}
      </h2>
      <div className="space-y-2">
        {scripts.length === 0 && !editing && (
          <p className="rounded-xl border border-line bg-bg-soft px-3 py-3 text-xs text-ink-faint">
            {t("js.empty")}
          </p>
        )}

        <ul className="space-y-2">
          {scripts.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-2 rounded-xl border border-line bg-bg-soft px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1">
                  <span className="truncate text-sm text-ink">{s.name}</span>
                  <span className="rounded bg-bg-raised px-1.5 py-0.5 text-xs text-ink-faint">
                    {LANG_LABELS[s.language === "lua" ? "lua" : "javascript"]}
                  </span>
                  {s.capabilities?.network && (
                    <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-300">
                      {t("js.network")}
                    </span>
                  )}
                  {s.capabilities?.storage && (
                    <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-300">
                      {t("js.storage")}
                    </span>
                  )}
                  {!s.enabled && (
                    <span className="rounded bg-bg-raised px-1.5 py-0.5 text-xs text-ink-faint">
                      {t("js.inactive")}
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-ink-faint">
                  {(s.hooks ?? []).map((h) => hookLabel(h, t)).join(", ") || t("js.noHooks")}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  className="rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                  onClick={() => setEditing(s)}
                >
                  {t("common.edit")}
                </button>
                <button
                  className="rounded-lg border border-line px-2 py-1 text-xs text-red-300 hover:bg-bg-raised"
                  onClick={() => remove(s)}
                >
                  {t("common.delete")}
                </button>
              </div>
            </li>
          ))}
        </ul>

        {editing ? (
          <JsScriptForm
            script={editing === "new" ? undefined : editing}
            onDone={async () => {
              setEditing(null);
              await reload();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <button
            className="w-full rounded-xl border border-dashed border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={() => setEditing("new")}
          >
            {t("js.add")}
          </button>
        )}
      </div>
    </section>
  );
}

function JsScriptForm({
  script,
  onDone,
  onCancel,
}: {
  script?: Script;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useT();
  const jsTemplate = defaultJsTemplate(t);
  const luaTemplate = defaultLuaTemplate(t);
  const [name, setName] = useState(script?.name ?? "");
  const [language, setLanguage] = useState<"javascript" | "lua">(
    script?.language === "lua" ? "lua" : "javascript",
  );
  const [source, setSource] = useState(
    script?.source ?? (script?.language === "lua" ? luaTemplate : jsTemplate),
  );
  const [hooks, setHooks] = useState<ScriptHook[]>(script?.hooks ?? ["after-response"]);
  const [enabled, setEnabled] = useState(script?.enabled ?? true);
  const [useVariables, setUseVariables] = useState(!!script?.capabilities?.variables);
  // 네트워크(중개 fetch)는 elevated 권한(결정 #17): 도메인 allowlist + 명시적 동의 필요.
  const [useNetwork, setUseNetwork] = useState(!!script?.capabilities?.network);
  const [domains, setDomains] = useState(
    (script?.capabilities?.network?.domains ?? []).join("\n"),
  );
  // 스토리지(영속 데이터)도 elevated 권한(결정 #17): 동의 필요.
  const [useStorage, setUseStorage] = useState(!!script?.capabilities?.storage);
  const [consent, setConsent] = useState(script?.consentGranted ?? false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /** elevated 권한(네트워크/스토리지) 사용 여부 — 동의가 필요한지 판단용. */
  const usesElevated = useNetwork || useStorage;

  const toggleHook = (h: ScriptHook) => {
    setHooks((cur) => (cur.includes(h) ? cur.filter((x) => x !== h) : [...cur, h]));
  };

  /** 언어 전환 시, 본문이 아직 기본 템플릿이면 새 언어 템플릿으로 교체(작성 내용 보존). */
  const switchLanguage = (lang: "javascript" | "lua") => {
    setSource((cur) =>
      cur === jsTemplate || cur === luaTemplate || cur.trim() === ""
        ? lang === "lua"
          ? luaTemplate
          : jsTemplate
        : cur,
    );
    setLanguage(lang);
  };

  /** 줄/콤마 구분 도메인 목록 → 정리된 배열(빈 줄·공백 제거). */
  const parsedDomains = (): string[] =>
    domains
      .split(/[\n,]/)
      .map((d) => d.trim())
      .filter(Boolean);

  const save = async (closeAfter = true) => {
    if (!name.trim() || !source.trim()) {
      setErr(t("js.errNameCodeRequired"));
      return;
    }
    if (hooks.length === 0) {
      setErr(t("js.errHooksRequired"));
      return;
    }
    const netDomains = parsedDomains();
    if (useNetwork && netDomains.length === 0) {
      setErr(t("js.errDomainsRequired"));
      return;
    }
    if (usesElevated && !consent) {
      setErr(t("js.errConsentRequired"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // capability 구성(결정 #17): safe(variables/mutateMessages)는 동의 불필요,
      // network(중개 fetch)/storage(영속)는 elevated라 consentGranted 필요.
      const capabilities: ScriptCapabilities = { mutateMessages: true };
      if (useVariables) capabilities.variables = true;
      if (useNetwork) capabilities.network = { domains: netDomains };
      if (useStorage) capabilities.storage = true;
      // 동의는 elevated 권한을 실제로 켰을 때만 의미가 있다.
      const consentGranted = usesElevated ? consent : false;
      if (script) {
        await storage.scripts.put({
          ...script,
          name: name.trim(),
          language,
          enabled,
          source,
          hooks,
          capabilities,
          consentGranted,
        });
      } else {
        const s = createScript({
          name: name.trim(),
          language,
          source,
          enabled,
          hooks,
          capabilities,
          consentGranted,
        });
        await storage.scripts.put(s);
      }
      if (closeAfter) await onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-xl border border-line bg-bg-soft p-3">
      <input
        className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
        placeholder={t("js.namePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <div>
        <span className="mb-1 block text-xs text-ink-soft">{t("js.language")}</span>
        <div className="flex gap-2 text-xs">
          {(["javascript", "lua"] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              className={`rounded-lg border px-3 py-1 ${
                language === lang
                  ? "border-accent bg-accent/15 text-ink"
                  : "border-line text-ink-soft hover:bg-bg-raised"
              }`}
              onClick={() => switchLanguage(lang)}
            >
              {LANG_LABELS[lang]}
            </button>
          ))}
        </div>
      </div>

      <CodeEditor
        value={source}
        onChange={setSource}
        language={LANG_LABELS[language]}
        aiKind={language}
        onSave={() => save(false)}
        placeholder={language === "lua" ? t("js.luaCode") : t("js.jsCode")}
        minHeight={160}
        inputBg="bg-bg-raised"
      />

      <ScriptTestPanel language={language} source={source} onApply={setSource} />

      {/* 버전 기록(로컬 git, 결정 #30): 저장된 스크립트만 추적(새 스크립트는 id 없음). */}
      {script && (
        <VersionHistoryPanel
          path={scriptPath(script.id, language)}
          value={source}
          onRestore={setSource}
        />
      )}


      <div>
        <span className="mb-1 block text-xs text-ink-soft">{t("js.hooks")}</span>
        <div className="flex flex-wrap gap-2 text-xs">
          {SUPPORTED_HOOKS.map((h) => (
            <label key={h} className="flex items-center gap-1 text-ink-soft">
              <input type="checkbox" checked={hooks.includes(h)} onChange={() => toggleHook(h)} />
              {hookLabel(h, t)}
            </label>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-ink-soft">
        <input
          type="checkbox"
          checked={useVariables}
          onChange={(e) => setUseVariables(e.target.checked)}
        />
        {t("js.useVariables")}
      </label>

      <div className="space-y-2 rounded-lg border border-line bg-bg-raised/40 p-2">
        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={useNetwork}
            onChange={(e) => {
              setUseNetwork(e.target.checked);
              if (!e.target.checked && !useStorage) setConsent(false);
            }}
          />
          {t("js.useNetwork")} <span className="text-amber-300">{t("js.elevated")}</span>
        </label>

        {useNetwork && (
          <div className="space-y-2 pl-6">
            <span className="mb-1 block text-xs text-ink-soft">
              {t("js.allowedDomains")}
            </span>
            <textarea
              className="h-16 w-full resize-y rounded-lg border border-line bg-bg-raised px-2 py-1.5 font-mono text-xs text-ink outline-none focus:border-accent"
              placeholder={"api.example.com\nexample.org"}
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              spellCheck={false}
            />
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={useStorage}
            onChange={(e) => {
              setUseStorage(e.target.checked);
              if (!e.target.checked && !useNetwork) setConsent(false);
            }}
          />
          {t("js.useStorage")} <span className="text-amber-300">{t("js.elevated")}</span>
        </label>

        {usesElevated && (
          <label className="flex items-start gap-2 pl-6 text-xs text-ink-soft">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span>
              {t("js.consent")}
              {useNetwork && (
                <>
                  {" "}{t("js.networkNoticeBefore")}{" "}
                  <code className="text-ink">
                    {language === "lua" ? "fetch(url, init):await()" : "await fetch(url, init)"}
                  </code>
                  {t("js.networkNoticeAfter")} <code className="text-ink">{"{ ok, status, body }"}</code>.
                </>
              )}
              {useStorage && (
                <>
                  {" "}{t("js.storageNoticeBefore")}{" "}
                  <code className="text-ink">
                    {language === "lua"
                      ? 'storageGet("k"):await() / storageSet("k", v):await()'
                      : 'storageGet("k") / storageSet("k", v)'}
                  </code>
                  .
                </>
              )}
            </span>
          </label>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-ink-soft">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        {t("common.enabled")}
      </label>

      <p className="text-xs text-ink-faint">
        {t("js.sandboxLimit")}
      </p>

      {err && <p className="text-xs text-red-300">{err}</p>}

      <div className="flex justify-end gap-2">
        <button
          className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
          onClick={onCancel}
          disabled={busy}
        >
          {t("common.cancel")}
        </button>
        <button
          className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
          onClick={() => save()}
          disabled={busy}
        >
          {busy ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}
